// jwt.interceptor.ts

import {
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import {
  catchError,
  filter,
  switchMap,
  take,
  finalize,
} from 'rxjs/operators';

import { User } from '../models';
import { AuthenticationService } from '../shared/services';
import { AppwriteService } from '../shared/services/appwrite.sdk.service';

// Shared state for token refresh
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

/**
 * JWT Interceptor
 * 
 * Automatically adds JWT token to requests and handles token refresh
 * when tokens expire or are invalid.
 * 
 * Features:
 * - Adds Authorization header to API requests
 * - Validates token before each request
 * - Automatically refreshes expired tokens
 * - Queues requests during token refresh
 * - Handles token refresh failures with logout
 * - Prevents infinite refresh loops using retry counter
 */
export const jwtInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<any> => {
  const authenticationService = inject(AuthenticationService);
  const appwriteService = inject(AppwriteService);

  const debugInfo: Record<string, any> = {
    'Request URL': req.url,
    'Method': req.method,
    'Has disableInterceptor': req.headers.get('disableInterceptor') === 'disable',
    'Has Authorization Header': req.headers.has('Authorization'),
    'Token Preview': authenticationService.currentTokenValue?.slice(0, 20) + '...',
    'User ID': authenticationService.currentUserValue?.id ?? 'No user',
    'Intercepted At': new Date().toLocaleTimeString(),
  };

  // Start a grouped log section for clarity
  console.groupCollapsed(
    `%c🔍 [JWT Interceptor] ${req.method} ${req.url}`,
    'color: #00bcd4; font-weight: bold;'
  );
  console.table(debugInfo);

  // Skip interception if explicitly disabled
  if (req.headers.get('disableInterceptor') === 'disable') {
    console.log('🟡 Skipping interceptor (disableInterceptor header found)');
    console.groupEnd();
    const modifiedReq = req.clone({
      headers: req.headers.delete('disableInterceptor'),
    });
    return next(modifiedReq);
  }

  const token = authenticationService.currentTokenValue;
  const currentUser: User = authenticationService.currentUserValue;

  if (!token) {
    console.warn('⚠️ No authentication token found, skipping JWT attachment.');
    console.log('Current User Value:', currentUser);
    console.groupEnd();
    return next(req);
  }

  if (isTokenValid(token, appwriteService, req.url)) {
    console.log('✅ Token valid — attaching token to request.');
    console.groupEnd();
    return handleRequestWithToken(req, next, token);
  }

  console.log('❌ Token valid check returned FALSE. Token:', token.slice(0, 10) + '...');

  console.log('🔄 Token invalid/expired — attempting refresh...');
  console.groupEnd();
  return handleTokenRefresh(req, next, authenticationService);
};



/**
 * Check if token is valid by parsing JWT expiry
 */
function isTokenValid(
  token: string,
  appwriteService: AppwriteService,
  url: string
): boolean {
  return appwriteService.isTokenValidParse(token, { URL: url });
}

/**
 * Handle request with valid token
 */
function handleRequestWithToken(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  token: string
): Observable<any> {
  // console.log("Token: ", token);
  const modifiedReq = addAuthorizationHeader(req, token);
  return next(modifiedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // If 401, 403 or 404, token might have been invalidated server-side
      if (error.status === 401 || error.status === 403 || error.status === 404) {

        // Prevent infinite loops
        if (req.headers.has('X-Retry-Count')) {
          console.error(`❌ Request failed with ${error.status} even after token refresh. Giving up.`);
          return throwError(() => error);
        }

        return handleTokenRefresh(
          req,
          next,
          inject(AuthenticationService)
        );
      }
      return throwError(() => error);
    })
  );
}

/**
 * Handle token refresh flow
 * Prevents multiple simultaneous refresh requests
 */
function handleTokenRefresh(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  authenticationService: AuthenticationService
): Observable<any> {
  // First request triggers refresh
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authenticationService.refreshTokenObservable().pipe(
      switchMap((newToken: string) => {
        // Broadcast new token to waiting requests
        refreshTokenSubject.next(newToken);

        // Retry original request with new token AND retry count
        const modifiedReq = addAuthorizationHeader(req, newToken).clone({
          setHeaders: { 'X-Retry-Count': '1' }
        });
        console.info('Token refreshed, retrying request:', req.url);
        return next(modifiedReq);
      }),
      catchError((error) => {
        // Refresh failed 
        console.error('Token refresh failed:', error);
        isRefreshing = false; // Reset flag immediately
        refreshTokenSubject.next(null); // Clear subject

        // Only logout if the refresh failed due to 401 (Cookie expired/invalid)
        // For other errors (network, 500), keep the session alive.
        if (error.status === 401 || error.code === 401 || error.status === 403) {
          authenticationService.logout(false);
        }

        return throwError(() => new Error('Token refresh failed'));
      }),
      finalize(() => {
        isRefreshing = false;
      })
    );
  }

  // Subsequent requests wait for refresh to complete
  return refreshTokenSubject.pipe(
    filter((token) => token !== null),
    take(1),
    switchMap((newToken) => {
      const modifiedReq = addAuthorizationHeader(req, newToken!).clone({
        setHeaders: { 'X-Retry-Count': '1' }
      });
      console.info('Using refreshed token for request:', req.url);
      return next(modifiedReq);
    })
  );
}

/**
 * Add Authorization header to request
 */
function addAuthorizationHeader(
  req: HttpRequest<any>,
  token: string
): HttpRequest<any> {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}